// "New board" dialog: pick a template (each previewed by rendering it).
import type { BoardInfo, WorkspaceInfo } from '../../shared/api.ts';
import type { BoardObject } from '../../shared/types.ts';
import { TEMPLATES, templateObjects, type TemplateId } from '../../shared/templates.ts';
import { post } from '../api.ts';
import { ImageCache } from '../engine/images.ts';
import { renderThumbnail } from '../export/scene.ts';
import { canvasMeasure, TextLayouts } from '../text/placement.ts';
import { h } from '../util/dom.ts';
import { formError } from './page.ts';

export function openTemplatePicker(ws: WorkspaceInfo): void {
  const error = formError();
  const grid = h('div', { class: 'template-grid', role: 'list' });
  const cancel = h('button', { type: 'button', class: 'btn' }, ['Cancel']);
  const dialog = h('dialog', { class: 'dialog templates', 'aria-labelledby': 'tpl-title' }, [
    h('h2', { id: 'tpl-title' }, ['New board']), error, grid, h('div', { class: 'dialog-actions' }, [cancel]),
  ]);
  cancel.addEventListener('click', () => dialog.close());
  dialog.addEventListener('close', () => dialog.remove());
  document.body.append(dialog);
  dialog.showModal();

  const layouts = new TextLayouts(canvasMeasure);
  const images = new ImageCache();
  for (const t of TEMPLATES) {
    const canvas = h('canvas', { class: 'template-preview', 'aria-hidden': 'true' });
    const card = h('button', { type: 'button', class: 'template-card', role: 'listitem' }, [
      canvas, h('strong', {}, [t.name]), h('span', { class: 'muted' }, [t.description]),
    ]);
    card.addEventListener('click', () => void create(t.id, t.id === 'blank' ? 'Untitled board' : t.name, card));
    grid.append(card);
    const objs = templateObjects(t.id, 'preview');
    const byId = new Map<string, BoardObject>(objs.map((o) => [o.id, o]));
    requestAnimationFrame(() => renderThumbnail(canvas, objs, { get: (id) => byId.get(id), layouts, images }, '#F7F7F5'));
  }

  async function create(template: TemplateId, title: string, card: HTMLButtonElement): Promise<void> {
    card.disabled = true;
    error.clear();
    try {
      const board = await post<BoardInfo>(`/api/workspaces/${ws.id}/boards`, { title, template });
      location.assign(`/b/${board.id}`);
    } catch (err) {
      card.disabled = false;
      error.show(err);
    }
  }
}
